import { Component, Children, createElement } from 'react';
import PropTypes from 'prop-types';
import { __, compose, either, any, prop, has, allPass, not, isEmpty, is, isNil } from 'ramda';

import FieldSet from 'components/FieldSet';

/*
  FieldSetContainer component

  This component passes some context data to the FieldSet component, but more importantly
  it works as an automatic system to hide the underlying FieldSet component when we know that none
  of the children are going to render (in view mode only).
  The functionality is so-so in terms of how react should be used and should be somehow taken into account
  when making the next big architectural modification. However, it works as intended and does not cause side-effects,
  but the implementation could be a bit more robust.

  The container checks whether we want the condensedView (by default, we do) and whether we are editing or not.
  In addition to these, the container checks whether any of the children (Fields) will render. It knows this by
  checking whether the field's name can be found in the formValues prop or if it is determined as a static field, which
  will always render. The properties formValues, editing and condensedView are all passed down by the FormProvider
  container.

  The element will brake in the following cases:
    1) It is used outside FormProvider
    2) It is given something else than FormField components or components that do not have a name or isStatic property.

  The component breaking in the situations above is by design. However, checks for those could be implemented
  to give better errors to the developer (TODO).
 */

/*
  A function that checks whether a child (Field) is going to be rendered based on its props.
 */

const willRender = values =>
  compose(
    either(
      either(prop('isStatic'), prop('computation')),
      compose(
        allPass([has(__, values), compose(not, isEmpty, prop(__, values)), compose(not, isNil, prop(__, values))]),
        prop('name'),
      ),
    ),
    prop('props'),
  );

class FieldSetContainer extends Component {
  componentWillMount() {
    const { children, onComponentRenders } = this.props;
    const { formValues, condensedView, editing } = this.context;

    if (condensedView && !editing) {
      if (!any(willRender(formValues), Children.toArray(children))) {
        is(Function, onComponentRenders) && onComponentRenders(false);
        this.shouldRender = false;
        return;
      }
    }

    is(Function, onComponentRenders) && onComponentRenders(true);
    this.shouldRender = true;
  }

  render() {
    const { children, ...other } = this.props;
    const { localeMessage } = this.context;
    return this.shouldRender ? createElement(FieldSet, { localeMessage, ...other }, children) : null;
  }
}

FieldSetContainer.contextTypes = {
  localeMessage: PropTypes.func,
  formValues: PropTypes.object,
  condensedView: PropTypes.bool,
  editing: PropTypes.bool,
};

FieldSetContainer.propTypes = {
  children: PropTypes.node,
  onComponentRenders: PropTypes.func,
};

export default FieldSetContainer;
